import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { X, Save } from 'lucide-react';

import {
  createRoutingRuleService,
  updateRoutingRuleService,
} from '../../utils/responsePartnerService';
import {
  upsertRoutingRule,
  setError as setReducerError,
} from '../../reducers/responsePartnerReducer';
import {
  ROUTING_SCOPES,
  TICKET_PRIORITIES,
  parseConditionsInput,
  formatConditions,
  normalizeIssueTypes,
} from '../../utils/responsePartnerUtils';
import IssueTypeTagEditor from './IssueTypeTagEditor';

const buildInitialForm = (rule) => ({
  responsePartnerId: rule?.responsePartnerId ?? '',
  scope: rule?.scope ?? 'global',
  scopeUserId: rule?.scopeUserId ?? '',
  scopePropertyKey: rule?.scopePropertyKey ?? '',
  issueTypes: normalizeIssueTypes(rule?.issueTypes),
  minPriority: rule?.minPriority ?? '',
  conditionsText: formatConditions(rule?.conditions ?? {}),
  priorityOrder: rule?.priorityOrder ?? 0,
  active: rule?.active !== false,
});

const RoutingRuleForm = ({ rule, partners = [], rules = [], onClose }) => {
  const dispatch = useDispatch();
  const isEditing = Boolean(rule?.id);
  const [form, setForm] = useState(() => buildInitialForm(rule));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const updateField = (field) => (event) => {
    const value = event?.target?.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleIssueTypesChange = (issueTypes) => {
    setForm((prev) => ({ ...prev, issueTypes }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (!form.responsePartnerId) {
      setError('Select a response partner');
      return;
    }
    if (!ROUTING_SCOPES.includes(form.scope)) {
      setError('Invalid scope');
      return;
    }

    let conditions;
    try {
      conditions = parseConditionsInput(form.conditionsText);
    } catch (parseError) {
      setError(parseError.message || 'Invalid conditions JSON');
      return;
    }

    const payload = {
      responsePartnerId: form.responsePartnerId,
      scope: form.scope,
      scopeUserId:
        form.scope === 'user' && String(form.scopeUserId || '').trim()
          ? String(form.scopeUserId).trim()
          : null,
      scopePropertyKey:
        form.scope === 'property' && String(form.scopePropertyKey || '').trim()
          ? String(form.scopePropertyKey).trim()
          : null,
      issueTypes: normalizeIssueTypes(form.issueTypes),
      minPriority: form.minPriority || null,
      conditions,
      priorityOrder: Number.isFinite(Number(form.priorityOrder)) ? Number(form.priorityOrder) : 0,
      active: Boolean(form.active),
    };

    if (form.scope === 'user' && !payload.scopeUserId) {
      setError('User scope requires scope_user_id');
      return;
    }
    if (form.scope === 'property' && !payload.scopePropertyKey) {
      setError('Property scope requires scope_property_key');
      return;
    }

    setSubmitting(true);
    try {
      const saved = isEditing
        ? await updateRoutingRuleService(rule.id, payload)
        : await createRoutingRuleService(payload);
      dispatch(upsertRoutingRule(saved));
      onClose?.();
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to save routing rule';
      setError(message);
      dispatch(setReducerError(message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <form
        onSubmit={handleSubmit}
        className="relative bg-white rounded-lg w-[640px] max-w-full max-h-[85vh] flex flex-col"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-lg font-semibold m-0">
            {isEditing ? 'Edit Routing Rule' : 'New Routing Rule'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-amp-track-section="Routing Rule Form"
            data-amp-track-action="Close_Click"
            data-amp-track-description="Close Routing Rule Form"
            className="bg-transparent text-stone-600 hover:text-stone-900 p-1 m-0"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-grow overflow-y-auto px-4 py-3 flex flex-col gap-3 text-sm">
          {error && (
            <div className="p-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">Response partner *</span>
              <select
                value={form.responsePartnerId}
                onChange={updateField('responsePartnerId')}
                required
                className="border border-stone-300 rounded-md px-2 py-1.5 bg-white"
              >
                <option value="" disabled>
                  Select a partner
                </option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.slug}) {p.ownerUserId ? '· user' : '· global'}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">Scope *</span>
              <select
                value={form.scope}
                onChange={updateField('scope')}
                className="border border-stone-300 rounded-md px-2 py-1.5 bg-white"
              >
                {ROUTING_SCOPES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {form.scope === 'user' && (
            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">Scope user id *</span>
              <input
                type="text"
                value={form.scopeUserId}
                onChange={updateField('scopeUserId')}
                className="border border-stone-300 rounded-md px-2 py-1.5 font-mono"
                placeholder="users.id"
              />
            </label>
          )}

          {form.scope === 'property' && (
            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">Scope property key *</span>
              <input
                type="text"
                value={form.scopePropertyKey}
                onChange={updateField('scopePropertyKey')}
                className="border border-stone-300 rounded-md px-2 py-1.5 font-mono"
                placeholder="property identifier"
              />
            </label>
          )}

          <div className="flex flex-col gap-1">
            <span className="font-semibold text-stone-700">Issue types</span>
            <IssueTypeTagEditor
              value={form.issueTypes}
              onChange={handleIssueTypesChange}
              partners={partners}
              rules={rules}
              selectedPartnerId={form.responsePartnerId}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">Minimum priority</span>
              <select
                value={form.minPriority || ''}
                onChange={updateField('minPriority')}
                className="border border-stone-300 rounded-md px-2 py-1.5 bg-white"
              >
                <option value="">Any</option>
                {TICKET_PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="font-semibold text-stone-700">Priority order</span>
              <input
                type="number"
                value={form.priorityOrder}
                onChange={updateField('priorityOrder')}
                className="border border-stone-300 rounded-md px-2 py-1.5"
                placeholder="0"
              />
              <span className="text-xs text-stone-500">Lower numbers run first.</span>
            </label>
          </div>

          <label className="flex flex-col gap-1">
            <span className="font-semibold text-stone-700">Conditions (JSON)</span>
            <textarea
              value={form.conditionsText}
              onChange={updateField('conditionsText')}
              rows={5}
              spellCheck={false}
              className="border border-stone-300 rounded-md px-2 py-1.5 font-mono text-xs"
              placeholder='{}'
            />
            <span className="text-xs text-stone-500">
              Optional structured conditions evaluated by the dispatch resolver.
            </span>
          </label>

          <label className="flex items-center gap-2 text-stone-700">
            <input
              type="checkbox"
              checked={Boolean(form.active)}
              onChange={updateField('active')}
            />
            <span className="font-semibold">Active</span>
          </label>
        </div>

        <div className="flex justify-end items-center gap-2 border-t px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-amp-track-section="Routing Rule Form"
            data-amp-track-action="Cancel_Click"
            data-amp-track-description="Cancel Routing Rule Form"
            className="flex items-center gap-2 bg-transparent text-stone-700 hover:bg-stone-200 rounded-md px-3 py-2 disabled:opacity-50"
          >
            <X size={18} /> Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-amp-track-section="Routing Rule Form"
            data-amp-track-action="Save_Click"
            data-amp-track-description="Save Routing Rule Form"
            className="flex items-center gap-2 bg-sky-600 text-white hover:bg-sky-500 rounded-md px-3 py-2 disabled:opacity-50"
          >
            <Save size={18} /> {submitting ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Rule'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RoutingRuleForm;
